import * as Sentry from '@sentry/browser';
import type { ErrorEvent } from '@sentry/types/types/event';

// 从package.json中获取版本号
import { version } from '../../../package.json';

/** 需要屏蔽的错误 */
const ignoreErrorsReg: RegExp[] = [
  /ResizeObserver loop/i, // 带这个关键字的错误是组件内部的 warning，不影响，但是报成了 error
  /Error: 未知错误/i, // 未知错误
  /Unexpected token '<'/i, // 版本更新历史文件无法加载
  /Failed to fetch/i, // 请求失败问题
  /Cannot read properties of undefined (reading 'getRawIndex')/i, // Echarts 报错
  /TypeError: undefined is not an object/i, // Echarts 报错
  /^TypeError: undefined is not an object \((.*)\)$/i, // Echarts 报错
  /TypeError: undefined is not an object \(evaluating '(.*)'\)/i,
  /TypeError: undefined is not an object (evaluating '__gCrWeb.edgeTranslate.detectPageState')/i, // 翻译报错
  /TypeError: undefined is not an object (evaluating 'a.L')/i,
  /TypeError: undefined is not an object (evaluating 'window.webkit.messageHandlers')/i,
  /TypeError: Cannot read properties of undefined \(reading '(.*)'\)/i,
  /TypeError: Cannot read properties of undefined (reading 'origin')/i,
  /TypeError: Cannot read properties of undefined (reading 'crx')/i,
  /TypeError: Cannot redefine property: .*/i,
  /Event `ErrorEvent` captured as exception with message `Script error.`/i,
  /AbortError: The user aborted a request/i,
  /Error: Objects are not valid as a React child/i,
  /ReferenceError/i,
  /TypeError: undefined is not iterable/i,
  /TypeError: undefined is not iterable (.*)/i,
  /ReferenceError: hint is not defined/i,
  /ReferenceError: Can't find variable: hint/i,
];

/** 允许上报的域名 */
const allowUrlsReg: RegExp[] = [
  /akms\.bluemediagroup\.cn/i, // 线上域名
];

/** 是否需要忽略此信息上报 */
export const isIgnoreError = (msg: string = '') => {
  return ignoreErrorsReg.some((reg) => reg.test(msg));
};

/** 当前域名是否可上报 */
export const isAllowUrl = (url: string = '') => {
  return allowUrlsReg.some((reg) => reg.test(url));
};

/** 初始化 Sentry 监控 */
export const initSentry = () => {
  Sentry.init({
    dsn: 'https://bb1a19b684fe451c8ae145a1883ba4b7@o4505418729521152.ingest.sentry.io/4505553865539584',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 1.0, // 采样率，范围在 0 ~ 1 之间，1 表示全部采样
    beforeSend(event: ErrorEvent, hint) {
      // 报错信息中添加版本号
      event.tags = event.tags || {};
      event.tags.version = version;

      const errMsg = event?.exception?.values?.[0]?.value;

      const messages = [
        'Non-Error exception captured',
        'Object captured as promise rejection with keys: errorFields, outOfDate, values',
        'Object captured as exception with keys: errorFields, outOfDate, values',
        'Object captured as promise rejection with keys: data, msg, status',
        'Non-Error promise rejection captured with keys: errorFields, outOfDate, values',
        'Objects are not valid as a React child',
        'NotFoundError: The object can not be found here',
        'InvalidAccessError: The object does not support the operation or argument',
        'TypeError: Cannot redefine property: googletag',
        'TypeError: Cannot redefine property: ethereum',
        'AbortError: The user aborted a request',
        'Error: Objects are not valid as a React child',
        'ReferenceError ucapi is not defined',
        'Error: Objects are not valid as a React child (found: object with keys {name, owner_name, number, code, ...',
        'TypeError: undefined is not iterable (cannot read property Symbol(Symbol.iterator))',
      ];

      const isNonErrorException = messages.some(
        (message) =>
          errMsg?.startsWith(message) ||
          // @ts-ignore
          hint?.originalException?.message?.startsWith(message),
      );

      if (isNonErrorException) {
        return null;
      }

      if (!errMsg) {
        return null;
      }
      // 1. 过滤 非生产上报
      if (!isAllowUrl(event?.request?.url || '')) {
        return null;
      }
      // 2. 过滤 降噪错误
      if (isIgnoreError(errMsg)) {
        return null;
      }
      return event;
    },
  });
};
